/**
 * Centralized logging utility that respects environment settings
 * Reduces production bundle size by eliminating unnecessary console outputs
 * Errors are also reported to Sentry when available
 */
import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  isDevelopment: boolean;
  enabledLevels: LogLevel[];
}

class Logger {
  private config: LogConfig;

  constructor() {
    this.config = {
      isDevelopment: process.env.NODE_ENV === 'development',
      enabledLevels: process.env.NODE_ENV === 'development' 
        ? ['debug', 'info', 'warn', 'error']
        : ['warn', 'error'] // Only warnings and errors in production
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabledLevels.includes(level);
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      console.log(`[INFO] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(`[ERROR] ${message}`, ...args);
    }
    // Always report errors to Sentry regardless of log level
    const errorArg = args.find(a => a instanceof Error);
    if (errorArg) {
      Sentry.captureException(errorArg);
    } else {
      Sentry.captureMessage(message, { level: 'error' });
    }
  }

  // Performance-specific logging
  performance(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(`[PERF] ${message}`, ...args);
    }
  }
}

export const logger = new Logger();